import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async'; // https://www.npmjs.com/package/react-helmet-async
import { connect, useDispatch } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { APP_NAME } from '../constants/app';
import { STATUS } from '../constants/avatar';
import DefaultLayout from "../layouts/DefaultLayout";
import LimitTimer from '../layouts/LimitTimer';
import AvatarConfirmedViewer from '../pages/AvatarConfirmedViewer';
import AvatarFirstLoad from '../pages/AvatarFirstLoad';
import ExpiredQR from '../pages/ExpiredQR';
import Home from "../pages/Home";
import QREntry from "../pages/QREntry";
import WrongAccess from '../pages/WrongAccess';
import { AppAction } from '../redux/actions/AppAction';
import { AvatarAction } from '../redux/actions/AvatarAction';
import { AppConstant, AvatarConstant } from '../redux/reducers';
import { store } from '../store';
import "./Transition.css";
import DeletedQR from '../pages/DeletedQR';

const Root = ({ pageTitle, pageAni, avatarItem, avatarLoading, auth, CheckAvatarId, avatarStatus, SetPageTitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: AppConstant.SET_CLEAR_ALL_SHOW });
  }, [dispatch]);

  // 앱 실행 시 아바타 확인 페이지(qr)가 아닌 경우 저장된 아바타의 재확인
  useEffect(() => {
    const isRunning = store.getState().AvatarReducer.loading;
    const isQRPage = window?.location?.pathname?.startsWith("/qr/");
    const existAvatarItem = store.getState().AuthReducer.existedData?.lastAvatar || {};
    if (auth?._id && auth?.region && !isRunning && !isQRPage) {
      const cb = async () => {
        if (existAvatarItem?.status === STATUS.DELETE &&  existAvatarItem?._id === auth?._id) {
          dispatch({ type: AvatarConstant.SET_AVATAR, item: { status: STATUS.DELETE }})
          return;
        }
        const ret = await CheckAvatarId(auth._id, auth.region);
        if (!ret?._id) {
          dispatch({ type: AvatarConstant.SET_AVATAR, item: { status: STATUS.WRONG }})
        }
      };
      cb();
    } else if (!isRunning && !isQRPage) {
      if (auth?._id) {
        dispatch({ type: AvatarConstant.SET_AVATAR, item: { status: STATUS.INIT } });
      } else {
        dispatch({ type: AvatarConstant.SET_AVATAR, item: { status: STATUS.WRONG }})
      }
    }
  }, [auth?._id, auth?.region]);

  // 아바타의 상태에 따른 라우팅
  useEffect(() => {
    if (!avatarLoading) {
      switch (avatarStatus) {
        case "":
        case STATUS.INIT:
        case STATUS.NEW:
        case STATUS.TERMS:
          if (avatarItem?._id) {
            if (avatarItem?.expiredAt && avatarItem?.expiredAt < new Date().getTime()) {
              dispatch({ type: AppConstant.SET_CLEAR_ALL_SHOW });
              navigate("/error/expired/qr");
            } else {
              dispatch({ type: AppConstant.SET_CLEAR_ALL_SHOW });
              navigate(`/qr/${avatarItem._id}/region/${avatarItem.region}`)
            }
          } else {
            navigate("/");
          }
          break;
        case STATUS.PROGRESSING:
        case STATUS.SYNCED:
          navigate("/avatar/firstLoad");
          dispatch({ type: AppConstant.SET_SHOW_TEAMS_CHECK, show: false });
          dispatch({ type: AppConstant.SET_SHOW_PASSWORD, show: false });
          break;
        case STATUS.CONFIRMED:
          navigate("/avatar/viewer");
          break;
        case STATUS.DELETE:
          navigate("/error/deleted/qr");
          break;
        case STATUS.WRONG:
          navigate("/error/wrong-access");
          break;
        default:
          // navigate("/");
          break;
      }
    }
  }, [dispatch, navigate, avatarItem?._id, avatarItem?.region, avatarLoading, avatarStatus, avatarItem?.expiredAt]);

  // 페이지에 따른 페이지 타이틀
  useEffect(() => {
    if (!location?.pathname) {
      SetPageTitle(APP_NAME);
    }
    if (location.pathname.startsWith("/qr/")) {
      SetPageTitle(APP_NAME + " - Welcome");
    } else if (location.pathname.startsWith("/avatar/firstLoad")) {
      SetPageTitle(APP_NAME + " - Welcome");
    } else if (location.pathname.startsWith("/avatar/viewer")) {
      SetPageTitle(APP_NAME + " - Viewer");
    } else if (location.pathname.startsWith("/error/wrong-access")) {
      SetPageTitle(APP_NAME + " - Wrong");
    } else {
      SetPageTitle(APP_NAME);
    }
  }, [SetPageTitle, location?.pathname]);

  return <>
    <Helmet>
      <title>{pageTitle}</title>
    </Helmet>
    <TransitionGroup className="transition-group">
      <CSSTransition key={pageAni ? location.pathname : null} classNames={pageAni} timeout={pageAni ? 500 : 0}>
        <Routes>
          <Route path="/" element={<DefaultLayout><Home /></DefaultLayout>} />
          <Route path="/qr/:id/region/:region" element={<DefaultLayout><QREntry /></DefaultLayout>} />
          <Route path="/avatar/firstLoad" element={<DefaultLayout><AvatarFirstLoad /></DefaultLayout>} />
          <Route path="/avatar/viewer" element={<DefaultLayout><AvatarConfirmedViewer /></DefaultLayout>} />
          <Route path="/error/wrong-access" element={<DefaultLayout><WrongAccess /></DefaultLayout>} />
          <Route path="/error/expired/qr" element={<DefaultLayout><ExpiredQR /></DefaultLayout>} />
          <Route path="/error/deleted/qr" element={<DefaultLayout><DeletedQR /></DefaultLayout>} />
          <Route path="*" element={<DefaultLayout><Home /></DefaultLayout>} />
        </Routes>
      </CSSTransition>
    </TransitionGroup>
    <LimitTimer />
  </>
};

const mapState = (state) => {
  const pageTitle = state.AppReducer.pageTitle;
  const pageAni = state.AppReducer.pageAni;
  const avatarItem = state.AvatarReducer.item;
  const avatarLoading = state.AvatarReducer.loading;
  const avatarStatus = state.AvatarReducer.status;
  const auth = state.AuthReducer.user;
  return { pageTitle, pageAni, avatarItem, avatarLoading, auth, avatarStatus };
};

const mapDispatch = dispatch => ({
  SetPageTitle: (title) => dispatch(AppAction.SetPageTitle(title)),
  CheckAvatarId: (id, region) => dispatch(AvatarAction.CheckAvatarId(id, region)),
});

export default connect(mapState, mapDispatch)(Root);
